import moment from 'moment-timezone'
import { useMutation } from '@apollo/client'
import { Table } from 'antd'
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons'
import styles from '@OpsQuality/styles.css'
import { downloadReport } from '@OpsQuality/logic'
import { printVisualFictiv5Quality as PrintVisualFictiv5ResultType } from '@OpsQuality/JobRequestDetails/__generated__/printVisualFictiv5Quality'
import { JobRequestAttachmentsWithCategoryType } from '@OpsQuality/types'

import Box from '~/core/Box'
import Tooltip from '~/core/Tooltip'
import Link from '~/core/Link'
import logger from '~/lib/logger'
import SafeDownloadLink from '~/apps/consumer/components/SafeDownloadLink'
import { FilesLayoutType } from '../types'
import { printVisualFictiv5Mutation } from '../../query'

export default function FilesTabLayout(props: FilesLayoutType) {
    const { files, isMobile } = props

    const [printFictiv5] = useMutation<PrintVisualFictiv5ResultType>(
        printVisualFictiv5Mutation,
    )

    const printVisualFictiv5 = async (
        event: Event,
        recordObj: JobRequestAttachmentsWithCategoryType,
    ) => {
        event.preventDefault()
        const url = await printFictiv5({
            variables: {
                id: recordObj.jobRequestId,
            },
        })
            .then(result => result?.data?.generateJobRequestVisualFictiv5)
            .catch((e: string) => logger.exception('Failed to print', e))

        if (url) {
            downloadReport(url)
        }
    }

    const staticColumns = [
        {
            title: 'File Name',
            dataIndex: 'fileName',
            key: 'fileName',
            render: (
                record: string,
                recordObj: JobRequestAttachmentsWithCategoryType,
            ) => recordObj?.attachment?.fileName,
        },
        {
            title: 'Part Name',
            key: 'partName',
            dataIndex: 'workOrderItemName',
            render: (
                record: string,
                recordObj: JobRequestAttachmentsWithCategoryType,
            ) => {
                if (!record && recordObj.partNames) {
                    return (
                        <Box>
                            {recordObj.partNames[0]}
                            <br />
                            <Tooltip
                                title={recordObj.partNames.slice(1).join(', ')}
                                style={{
                                    width: '30px',
                                }}
                                trigger="hover"
                                placement="bottomLeft"
                            >
                                {recordObj.partNames.length > 1 ? (
                                    <small>{`Show ${
                                        recordObj.partNames.length - 1
                                    } more`}</small>
                                ) : null}
                            </Tooltip>
                        </Box>
                    )
                }
                return record
            },
        },
        {
            title: 'Upload Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (record: string) =>
                record && moment(record).format('ddd, MMM Do YYYY, h:mm A'),
        },
        {
            title: 'Upload Time',
            dataIndex: 'createdAt',
            key: 'createdAtTime',
            render: (record: string) =>
                record && moment(record).format('h:mm A'),
        },
        {
            title: 'Uploaded By',
            dataIndex: 'createdBy',
            key: 'displayName',
            render: (
                record: string,
                recordObj: JobRequestAttachmentsWithCategoryType,
            ) => recordObj?.createdBy?.displayName,
        },
        {
            title: 'Actions',
            dataIndex: 'url',
            key: 'url',
            render: (
                record: string,
                recordObj: JobRequestAttachmentsWithCategoryType,
            ) => {
                const {
                    partNames = [],
                    attachment,
                    visibleToCustomer,
                } = recordObj || {}

                return (
                    <Box alignItems="center">
                        {partNames.length > 0 ? (
                            <Link
                                name="downloadLink"
                                placement="navigation"
                                onClick={(event: Event) =>
                                    printVisualFictiv5(event, recordObj)
                                }
                            >
                                Download
                            </Link>
                        ) : (
                            <SafeDownloadLink
                                name="downloadLink"
                                fileName={attachment?.fileName}
                                downloadUrl={attachment?.downloadUrl}
                                placement="navigation"
                            >
                                Download
                            </SafeDownloadLink>
                        )}
                        {visibleToCustomer ? (
                            <EyeOutlined
                                className={`${styles.visibiltyIconStyle} ${styles.visibilityOnStyle}`}
                            />
                        ) : (
                            <EyeInvisibleOutlined
                                className={`${styles.visibiltyIconStyle} ${styles.visibilityOffStyle}`}
                            />
                        )}
                    </Box>
                )
            },
        },
    ]

    return (
        <Box direction="col" backgroundColor="white">
            <Table
                rowKey={record => record?.attachment?.id}
                columns={isMobile ? [staticColumns[0]] : staticColumns}
                pagination={false}
                dataSource={files}
                rowClassName={record =>
                    record.visibleToCustomer === false
                        ? styles.disabledRowStyle
                        : ''
                }
                expandedRowRender={
                    isMobile
                        ? (record: JobRequestAttachmentsWithCategoryType) => (
                              <Box>
                                  <Box className={styles.expandedRowHeader}>
                                      Part Name
                                  </Box>
                                  <Box>{record?.workOrderItemName}</Box>
                                  <Box className={styles.expandedRowHeader}>
                                      Upload Date
                                  </Box>
                                  <Box>
                                      {moment(record?.createdAt).format(
                                          'ddd, MMM Do YYYY, h:mm A',
                                      )}
                                  </Box>
                                  <Box className={styles.expandedRowHeader}>
                                      Uploaded By
                                  </Box>
                                  <Box>{record?.createdBy?.displayName}</Box>
                                  <Box className={styles.expandedRowHeader}>
                                      Actions
                                  </Box>
                                  <Box>
                                      <Link
                                          name="downloadLink"
                                          placement="navigation"
                                          href={record?.attachment?.downloadUrl}
                                          target="_blank"
                                      >
                                          Download
                                      </Link>
                                  </Box>
                              </Box>
                          )
                        : undefined
                }
            />
        </Box>
    )
}
